import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../services/api/axios.config';
import { notify } from '../utils/methods/notification';

const initialState = {
    isLoading: false,
    allLeukapheresisConditions: [],
    leukapheresisConditionsDropdown: [],
    addModal: false
};

// fetch all Leukapheresis conditions
export const fetchAllLeukapheresisConditions = createAsyncThunk(
    'bmr/fetchAllLeukapheresisConditions',
    async (_, { rejectWithValue }) => {
        try {
            const response = await api.get('/leukapheresis_conditions');
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response ? error.response.data : error.message);
        }
    }
);

// add Leukapheresis condition
export const addLeukapheresisCondition = createAsyncThunk(
    'bmr/addLeukapheresisCondition',
    async (args, { rejectWithValue, dispatch }) => {
        try {
            const response = await api.post('/leukapheresis_conditions/create', args);
            notify.success(response.data.message);
            dispatch(disableAddModal());
            dispatch(fetchAllLeukapheresisConditions());
            return response.data;
        } catch (error) {
            notify.error(error || error.message);
            return rejectWithValue(error.response ? error.response.data : error.message);
        }
    }
);

// edit Leukapheresis condition
export const editLeukapheresisCondition = createAsyncThunk(
    'bmr/editLeukapheresisCondition',
    async (args, { rejectWithValue, dispatch }) => {
        try {
            const { data, editId } = args;
            const response = await api.put(`/leukapheresis_conditions/update/${editId}`, data);
            notify.success(response.data.message);
            dispatch(disableAddModal());
            dispatch(fetchAllLeukapheresisConditions());
            return response.data;
        } catch (error) {
            notify.error(error?.message || error);
            return rejectWithValue(error.response ? error.response.data : error.message);
        }
    }
);

// delete Leukapheresis condition
export const deleteLeukapheresisCondition = createAsyncThunk(
    'bmr/deleteLeukapheresisCondition',
    async (args, { rejectWithValue, dispatch }) => {
        try {
            const { id } = args;
            const response = await api.delete(`/leukapheresis_conditions/${id}`);
            notify.success(response.data.message);
            dispatch(fetchAllLeukapheresisConditions());
            return response.data;
        } catch (error) {
            notify.error(error?.message || error);
            return rejectWithValue(error.response ? error.response.data : error.message);
        }
    }
);

// @info get Esential patient for the portal 1
export const fetchLeukapheresisConditionsDropDown = createAsyncThunk(
    'bmr/fetchLeukapheresisConditionsDropDown',
    async (_, { rejectWithValue }) => {
        try {
            const response = await api.get(`/leukapheresis_conditions/active/status`);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response ? error.response.data : error.message);
        }
    }
);

const bmrLeukapheresisConditionsSlice = createSlice({
    name: 'bmrLeukapheresisConditions',
    initialState,
    reducers: {
        enableAddModal(state) {
            state.addModal = true;
        },
        disableAddModal(state) {
            state.addModal = false;
        },
    },
    extraReducers: (builder) => {
        builder
            // fetch
            .addCase(fetchAllLeukapheresisConditions.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(fetchAllLeukapheresisConditions.fulfilled, (state, action) => {
                state.isLoading = false;
                state.allLeukapheresisConditions = action.payload.data;
            })
            .addCase(fetchAllLeukapheresisConditions.rejected, (state) => {
                state.isLoading = false;
            })
            // add
            .addCase(addLeukapheresisCondition.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(addLeukapheresisCondition.fulfilled, (state) => {
                state.isLoading = false;
            })
            .addCase(addLeukapheresisCondition.rejected, (state) => {
                state.isLoading = false;
            })
            // edit
            .addCase(editLeukapheresisCondition.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(editLeukapheresisCondition.fulfilled, (state) => {
                state.isLoading = false;
            })
            .addCase(editLeukapheresisCondition.rejected, (state) => {
                state.isLoading = false;
            })
            // delete
            .addCase(deleteLeukapheresisCondition.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(deleteLeukapheresisCondition.fulfilled, (state) => {
                state.isLoading = false;
            })
            .addCase(deleteLeukapheresisCondition.rejected, (state) => {
                state.isLoading = false;
            })
            // dropdown
            .addCase(fetchLeukapheresisConditionsDropDown.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(fetchLeukapheresisConditionsDropDown.fulfilled, (state,action) => {
                state.isLoading = false;
                state.leukapheresisConditionsDropdown = action.payload.data.items?.map(op => { return { value: op?.id, label: op.name } })
            })
            .addCase(fetchLeukapheresisConditionsDropDown.rejected, (state) => {
                state.isLoading = false;
            });
    },
});

export const { disableAddModal, enableAddModal } = bmrLeukapheresisConditionsSlice.actions;

export const selectLeukapheresisConditionsDropdown = (state) => state.bmrLeukapheresisConditions.leukapheresisConditionsDropdown;
export const selectLeukapheresisConditionsLoading = (state) => state.bmrLeukapheresisConditions.isLoading;
export const selectLeukapheresisConditionsAll = (state) => state.bmrLeukapheresisConditions.allLeukapheresisConditions;
export const selectBmrAddLeukapheresisConditionModal = (state) => state.bmrLeukapheresisConditions.addModal;

export default bmrLeukapheresisConditionsSlice.reducer;
